import { useQuery } from "@apollo/client";
import { Title, Text } from "@mantine/core";
import { format } from "date-fns";
import { parseDateOnly } from "shared/src/utils/dateUtils";
import { gql } from "@/__generated__";
import Loader from "@/components/shared/Loader";
import { StatusIndicator } from "@/components/StatusIndicator/StatusIndicator";
import styles from "./strengthen-referral-header.module.scss";

export const GET_PATIENT_INFO = gql(`
  query GetPatientInfoForStrengthen($appealId: ID!) {
    getAppealById(appealId: $appealId) {
      appealId
      referralPatientEmail
      sponsorshipExpiredTimestamp
      patient {
        firstName
        lastName
        dateOfBirth
      }
      denial {
        condition
        medicationBrandName
      }
      status {
        referred
        insuranceCardUpload
        insuranceCardUploadSkipped
        paymentSuccessful
        issueOutstanding
        denialUpheld
        denialOverturned
        appealSubmitted
      }
    }
  }
`);

interface Props {
  appealId: string;
}

export function StrengthenReferralHeader({ appealId }: Props) {
  const { data, loading, error } = useQuery(GET_PATIENT_INFO, {
    variables: { appealId },
    fetchPolicy: "no-cache",
  });

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Loader color="loader_almost_black" width="36" height="36" />
      </div>
    );
  }

  if (error || !data?.getAppealById) {
    return (
      <div className={styles.header}>
        <Title order={2}>Strengthen Referral</Title>
        <Text c="red">Unable to load patient information</Text>
      </div>
    );
  }

  const appeal = data.getAppealById;
  const patient = appeal.patient;
  const denial = appeal.denial;

  const patientName = patient
    ? `${patient.firstName} ${patient.lastName}`
    : "Unknown";
  const dateOfBirth = patient?.dateOfBirth
    ? format(parseDateOnly(patient.dateOfBirth), "MM/dd/yyyy")
    : "N/A";
  const email = appeal.referralPatientEmail || "N/A";

  return (
    <div className={styles.header}>
      <div className={styles.titleRow}>
        <div>
          <Title order={2} className={styles.title}>
            {patientName}
          </Title>
          <Text className={styles.appealId}>ID: {appeal.appealId}</Text>
        </div>
        <StatusIndicator
          status={appeal.status}
          sponsorshipExpiredTimestamp={appeal.sponsorshipExpiredTimestamp}
        />
      </div>

      <div className={styles.patientInfoContainer}>
        <div className={styles.patientInfoItem}>
          <Text className={styles.label}>Patient name</Text>
          <Text className={styles.value}>{patientName}</Text>
        </div>
        <div className={styles.patientInfoItem}>
          <Text className={styles.label}>DoB</Text>
          <Text className={styles.value}>{dateOfBirth}</Text>
        </div>
        <div className={styles.patientInfoItem}>
          <Text className={styles.label}>Email</Text>
          <Text className={styles.value}>{email}</Text>
        </div>
        <div className={styles.patientInfoItem}>
          <Text className={styles.label}>Condition</Text>
          <Text className={styles.value}>{denial?.condition || "N/A"}</Text>
        </div>
        <div className={styles.patientInfoItem}>
          <Text className={styles.label}>Medication</Text>
          <Text className={styles.value}>
            {denial?.medicationBrandName || "N/A"}
          </Text>
        </div>
      </div>
    </div>
  );
}
